import {
  buildConfigurationFingerprint,
  deriveDoorGeometry,
  getDoorProductPreset,
  normalizeDoorConfig,
} from './door-model';
import doubleProductSchema from './data/backend/obojstranne-ukonceny-profil.schema.json';
import fullProductSchema from './data/backend/obojstranne-otvoreny-profil.schema.json';
import edgeProductSchema from './data/backend/polootvoreny-profil.schema.json';
import { createReferenceConfiguratorState, REFERENCE_TCN_SOURCE } from './configurator-reference';
import { analyzeTcnCompatibility } from './configurator-tcn';
import { PRICING_VERSION } from './configurator-version';
import type {
  ConfiguratorState,
  ConfiguratorCatalog,
  ConfiguratorProductDefinition,
  FinishId,
  HandleVariant,
  MachineOption,
  MaterialOption,
  PriceEstimate,
  ProductOption,
} from './configurator-types';

export const MATERIALS: MaterialOption[] = [
  {
    id: 'wood',
    label: 'Drevo',
    subtitle: 'Dub, masív a drevené dosky',
    color: '#9a6840',
    sideColor: '#654126',
    textColor: '#fffaf2',
    stockCostPerM3: 5_000,
    wasteFactor: 1.15,
    machiningRatePerHour: 60,
    setupCost: 18,
    density: '690 kg/m³',
    finish: 'Prírodná kresba',
  },
  {
    id: 'plastic',
    label: 'Plast',
    subtitle: 'POM, PE a technické plasty',
    color: '#e7e4dc',
    sideColor: '#b8b5ad',
    textColor: '#1e2722',
    stockCostPerM3: 12_000,
    wasteFactor: 1.1,
    machiningRatePerHour: 65,
    setupCost: 20,
    density: '1 410 kg/m³',
    finish: 'Jemný mat',
  },
  {
    id: 'zinc',
    label: 'Zinok',
    subtitle: 'Zinkové a pozinkované dielce',
    color: '#a8b3b4',
    sideColor: '#707b7d',
    textColor: '#13201d',
    stockCostPerM3: 35_000,
    wasteFactor: 1.08,
    machiningRatePerHour: 95,
    setupCost: 28,
    density: '7 140 kg/m³',
    finish: 'Patinovaný kov',
  },
  {
    id: 'aluminium',
    label: 'Hliník',
    subtitle: 'Ľahké presné CNC dielce',
    color: '#c9d0d0',
    sideColor: '#848e90',
    textColor: '#15211d',
    stockCostPerM3: 22_000,
    wasteFactor: 1.08,
    machiningRatePerHour: 90,
    setupCost: 25,
    density: '2 700 kg/m³',
    finish: 'Brúsený kov',
  },
];

const LOCAL_PRODUCT_SCHEMAS = [edgeProductSchema, fullProductSchema, doubleProductSchema] as const;

export const LOCAL_PRODUCTS: ProductOption[] = LOCAL_PRODUCT_SCHEMAS.map(schema => ({
  id: schema.product.id as ProductOption['id'],
  label: schema.product.name,
  subtitle: schema.product.description,
  status: schema.product.status === 'soon' ? 'soon' : 'available',
}));

export const LOCAL_MACHINES: MachineOption[] = [
  {
    id: 'bdv-5axis',
    label: 'CNC vyrezávací stroj',
    description:
      'Všeobecný výrobný profil pre vyrezávanie a frézovanie nakonfigurovaných dielcov. Presný model a technologické možnosti doplníme podľa podkladov klienta.',
    imageAlt: 'CNC vyrezávací stroj',
  },
];

export const FINISHES: Array<{ id: FinishId; label: string; description: string }> = [
  { id: 'natural', label: 'Prírodný', description: 'Bez povrchovej úpravy' },
  { id: 'oil', label: 'Olej', description: 'Zvýraznená kresba dreva' },
  { id: 'matte', label: 'Matný lak', description: 'Odolný jemný povrch' },
  { id: 'technical', label: 'Technický', description: 'Povrch podľa materiálu' },
];

export const HANDLE_DEPTH_CONTROL_MAX_MM = 17.3;
export const DEFAULT_CONFIG: ConfiguratorState = createReferenceConfiguratorState();

export const DEFAULT_PRODUCT_SLUGS: Record<ConfiguratorState['productId'], string> = {
  'door-handle-edge': 'polootvoreny-profil',
  'door-handle-full': 'obojstranne-otvoreny-profil',
  'door-handle-double': 'obojstranne-ukonceny-profil',
};

const LOCAL_PRODUCT_DEFINITIONS: ConfiguratorProductDefinition[] = LOCAL_PRODUCT_SCHEMAS.map(
  schema => {
    const productId = schema.product.id as ConfiguratorState['productId'];
    const option = LOCAL_PRODUCTS.find(product => product.id === productId)!;
    const defaults = schema.defaults as unknown as ConfiguratorState;

    return {
      slug: schema.product.configuratorSlug,
      option,
      defaults: normalizeDoorConfig({
        ...DEFAULT_CONFIG,
        ...defaults,
        productId,
        handle: { ...DEFAULT_CONFIG.handle, ...defaults.handle },
        edgeTreatment: { ...DEFAULT_CONFIG.edgeTreatment, ...defaults.edgeTreatment },
        stockAllowanceMm: { ...DEFAULT_CONFIG.stockAllowanceMm, ...defaults.stockAllowanceMm },
      }),
      schemaVersion: schema.schemaVersion,
    };
  },
);

export const LOCAL_CONFIGURATOR_CATALOG: ConfiguratorCatalog = {
  source: 'local',
  products: LOCAL_PRODUCTS,
  machines: LOCAL_MACHINES,
  productDefinitions: LOCAL_PRODUCT_DEFINITIONS,
};

/** Live bindings consumed by the client UI after its server-loaded catalog is installed. */
export let PRODUCTS: ProductOption[] = LOCAL_PRODUCTS;
export let MACHINES: MachineOption[] = LOCAL_MACHINES;
let productDefinitions: ConfiguratorProductDefinition[] = LOCAL_PRODUCT_DEFINITIONS;

export function setConfiguratorCatalog(catalog?: ConfiguratorCatalog | null) {
  const next = catalog ?? LOCAL_CONFIGURATOR_CATALOG;
  PRODUCTS = next.products.length ? next.products : LOCAL_PRODUCTS;
  MACHINES = next.machines.length ? next.machines : LOCAL_MACHINES;
  productDefinitions = next.productDefinitions.length
    ? next.productDefinitions
    : LOCAL_PRODUCT_DEFINITIONS;
}

export function getDefaultConfiguratorConfig() {
  return cloneConfiguratorState(productDefinitions[0]?.defaults ?? DEFAULT_CONFIG);
}

export function getProductPreviewConfig(productId: ConfiguratorState['productId']) {
  const definition = productDefinitions.find(item => item.option.id === productId);
  if (definition) return cloneConfiguratorState(definition.defaults);

  return normalizeDoorConfig({
    ...DEFAULT_CONFIG,
    ...getDoorProductPreset(DEFAULT_CONFIG, productId),
  });
}

export function getProductSelectionPreset(
  config: ConfiguratorState,
  productId: ConfiguratorState['productId'],
): Partial<ConfiguratorState> {
  const definition = productDefinitions.find(item => item.option.id === productId);
  if (!definition) return getDoorProductPreset(config, productId);

  const defaults = definition.defaults;
  const canonical = normalizeDoorConfig({
    ...config,
    productId,
    machineId: defaults.machineId,
    materialId: defaults.materialId,
    widthMm: defaults.widthMm,
    heightMm: defaults.heightMm,
    thicknessMm: defaults.thicknessMm,
    quantity: defaults.quantity,
    handle: { ...defaults.handle },
    edgeTreatment: { ...defaults.edgeTreatment },
    stockAllowanceMm: { ...defaults.stockAllowanceMm },
    finishId: defaults.finishId,
  });

  return {
    productId: canonical.productId,
    machineId: canonical.machineId,
    materialId: canonical.materialId,
    widthMm: canonical.widthMm,
    heightMm: canonical.heightMm,
    thicknessMm: canonical.thicknessMm,
    quantity: canonical.quantity,
    handle: canonical.handle,
    edgeTreatment: canonical.edgeTreatment,
    stockAllowanceMm: canonical.stockAllowanceMm,
    finishId: canonical.finishId,
  };
}

function cloneConfiguratorState(config: ConfiguratorState): ConfiguratorState {
  return {
    ...config,
    handle: { ...config.handle },
    edgeTreatment: { ...config.edgeTreatment },
    stockAllowanceMm: { ...config.stockAllowanceMm },
  };
}

export function getMaterial(id: ConfiguratorState['materialId']) {
  return MATERIALS.find(material => material.id === id) ?? MATERIALS[0];
}

export function getProduct(id: ConfiguratorState['productId']) {
  return PRODUCTS.find(product => product.id === id) ?? PRODUCTS[0];
}

export function getMachine(id: ConfiguratorState['machineId']) {
  return MACHINES.find(machine => machine.id === id) ?? MACHINES[0];
}

const VAT_RATE = 0.23;
const REFERENCE_STOCK_AREA_M2 = (429 * 276) / 1_000_000;
const REFERENCE_STOCK_PERIMETER_M = (2 * (429 + 276)) / 1_000;
const REFERENCE_FINISHED_PERIMETER_M = (2 * (426 + 276)) / 1_000;
const REFERENCE_HANDLE_LENGTH_MM: Record<HandleVariant, number> = {
  'edge-terminated': 285,
  'full-edge': 426,
  'double-terminated': 285,
};
const HANDLE_MINUTES: Record<HandleVariant, number> = {
  'edge-terminated': 9,
  'full-edge': 8.5,
  'double-terminated': 11,
};
const EDGE_MINUTES: Record<ConfiguratorState['edgeTreatment']['profile'], number> = {
  square: 0,
  chamfer: 2.25,
  roundover: 3,
};
const FINISHING_RATE_PER_M2: Record<FinishId, number> = {
  natural: 0,
  oil: 48,
  matte: 68,
  technical: 28,
};

export function calculateEstimate(config: ConfiguratorState): PriceEstimate {
  const canonical = normalizeDoorConfig(config);
  const materialOption = getMaterial(canonical.materialId);
  const geometry = deriveDoorGeometry(canonical);
  const quantity = canonical.quantity;

  const stockWidthM = geometry.stock.widthMm / 1_000;
  const stockHeightM = geometry.stock.heightMm / 1_000;
  const stockThicknessM = geometry.stock.thicknessMm / 1_000;
  const stockAreaM2 = stockWidthM * stockHeightM;
  const stockPerimeterM = 2 * (stockWidthM + stockHeightM);
  const stockVolumeM3 = stockAreaM2 * stockThicknessM;
  const finishedAreaM2 = (geometry.finished.widthMm * geometry.finished.heightMm) / 1_000_000;
  const finishedPerimeterM = (2 * (geometry.finished.widthMm + geometry.finished.heightMm)) / 1_000;
  const treatedSurfaceM2 =
    2 * finishedAreaM2 + finishedPerimeterM * (geometry.finished.thicknessMm / 1_000);

  // The supplied TCN already defines a repeatable product. We therefore price
  // estimated cycle time, not a fresh CAM development fee for every piece.
  const areaScale = Math.sqrt(Math.max(0.2, stockAreaM2 / REFERENCE_STOCK_AREA_M2));
  const perimeterScale = stockPerimeterM / REFERENCE_STOCK_PERIMETER_M;
  const finishedPerimeterScale = finishedPerimeterM / REFERENCE_FINISHED_PERIMETER_M;
  const thicknessScale = Math.sqrt(Math.max(0.5, geometry.stock.thicknessMm / 20));
  const handleLengthScale = clampNumber(
    geometry.handle.lengthMm / REFERENCE_HANDLE_LENGTH_MM[geometry.variant],
    0.5,
    6,
  );
  const handleDepthScale = Math.sqrt(clampNumber(geometry.handle.depthMm / 17.5, 0.35, 5.5));
  const edgeSizeScale =
    canonical.edgeTreatment.profile === 'square'
      ? 0
      : clampNumber(canonical.edgeTreatment.sizeMm / 2, 0.75, 2.5);
  const handleMinutes = HANDLE_MINUTES[geometry.variant] * handleLengthScale * handleDepthScale;
  const formattingMinutes = 6 * perimeterScale * thicknessScale;
  const edgeMinutes =
    EDGE_MINUTES[canonical.edgeTreatment.profile] * finishedPerimeterScale * edgeSizeScale;
  const handlingAndInspectionMinutes = 4 + 2 * areaScale;
  const machiningMinutesPerPiece =
    handleMinutes + formattingMinutes + edgeMinutes + handlingAndInspectionMinutes;

  const materialCost = roundPrice(
    stockVolumeM3 * materialOption.stockCostPerM3 * materialOption.wasteFactor * quantity,
  );
  const machining = roundPrice(
    (machiningMinutesPerPiece / 60) * materialOption.machiningRatePerHour * quantity,
  );
  const setup = roundPrice(
    materialOption.setupCost +
      (geometry.variant === 'double-terminated' ? 4 : geometry.variant === 'full-edge' ? 1.5 : 0),
  );
  const finishing = roundPrice(
    treatedSurfaceM2 * FINISHING_RATE_PER_M2[canonical.finishId] * quantity,
  );

  // Totals are built from the same rounded line items that the UI and PDF show.
  const subtotal = roundPrice(materialCost + machining + setup + finishing);
  const vat = roundPrice(subtotal * VAT_RATE);
  const total = roundPrice(subtotal + vat);

  return {
    pricingVersion: PRICING_VERSION,
    currency: 'EUR',
    vatRate: VAT_RATE,
    material: materialCost,
    machining,
    setup,
    finishing,
    subtotal,
    vat,
    total,
    unitPriceExcludingVat: roundPrice(subtotal / quantity),
    unitPrice: roundPrice(total / quantity),
    leadTime: quantity > 8 ? '10–14 pracovných dní' : '6–9 pracovných dní',
  };
}

function roundPrice(value: number) {
  return Math.round(value * 100) / 100;
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function buildConfigurationPayload(config: ConfiguratorState) {
  const estimate = calculateEstimate(config);
  const geometry = deriveDoorGeometry(config);
  const tcnCompatibility = analyzeTcnCompatibility(config);
  const projectStatus =
    tcnCompatibility.releaseState === 'invalid-configuration'
      ? 'invalid'
      : 'ready-for-technologist-review';

  return {
    schema: 'bdvtrade.configuration/v2',
    schemaId: 'bdvtrade.configuration/v2',
    configurationFingerprint: buildConfigurationFingerprint(config),
    generatedAt: new Date().toISOString(),
    project: {
      productId: config.productId,
      name: getProduct(config.productId).label,
      quantity: config.quantity,
      status: projectStatus,
    },
    manufacturing: {
      productId: config.productId,
      product: getProduct(config.productId).label,
      handleVariantId: geometry.variant,
      handleVariant: geometry.variant,
      machineId: config.machineId,
      machine: getMachine(config.machineId).label,
      materialId: config.materialId,
      material: getMaterial(config.materialId).label,
      finishId: config.finishId,
      finishedDimensionsMm: geometry.finished,
      stockAllowanceMm: {
        widthMm: config.stockAllowanceMm.widthMm,
        heightMm: config.stockAllowanceMm.heightMm,
        thicknessMm: config.stockAllowanceMm.thicknessMm,
      },
      stockDimensionsMm: geometry.stock,
      handle: {
        variantId: geometry.variant,
        edgeId: 'top',
        edge: 'top',
        openSideId: geometry.variant === 'edge-terminated' ? config.handle.openSide : null,
        openSide: geometry.variant === 'edge-terminated' ? config.handle.openSide : null,
        input: {
          openSide: config.handle.openSide,
          lengthMm: config.handle.lengthMm,
          offsetMm: config.handle.offsetMm,
          faceWidthMm: config.handle.faceWidthMm,
          depthMm: config.handle.depthMm,
          transitionLengthMm: config.handle.transitionLengthMm,
          transitionRoundness: config.handle.transitionRoundness,
          grooveWidthMm: config.handle.grooveWidthMm,
          roughingAllowanceMm: config.handle.roughingAllowanceMm,
        },
        startMm: geometry.handle.startMm,
        endMm: geometry.handle.endMm,
        lengthMm: geometry.handle.lengthMm,
        offsetMm: geometry.handle.offsetMm,
        faceWidthMm: geometry.handle.faceWidthMm,
        programmedDepth: geometry.handle.depthMm,
        roughingDepthMm: Math.max(0, geometry.handle.depthMm - config.handle.roughingAllowanceMm),
        roughingAllowanceMm: config.handle.roughingAllowanceMm,
        remainingWallMm: geometry.handle.remainingWallMm,
        transitionLengthMm: geometry.handle.transitionLengthMm,
        transitionRoundness: geometry.handle.transitionRoundness,
        designGrooveWidthMm: geometry.handle.grooveWidthMm,
        productionWidthMapping: 'tool-table:1009 / pending-confirmation',
        caps: {
          left: geometry.handle.capLeft,
          right: geometry.handle.capRight,
        },
        openings: {
          left: geometry.handle.openLeft,
          right: geometry.handle.openRight,
        },
      },
      edgeTreatment: {
        profileId: config.edgeTreatment.profile,
        profile: geometry.edge.profile,
        sizeMm: config.edgeTreatment.sizeMm,
        cornerRadiusMm: config.edgeTreatment.cornerRadiusMm,
      },
      finish: config.finishId,
      tcn: {
        dialectId: REFERENCE_TCN_SOURCE.dialectId,
        dialect: REFERENCE_TCN_SOURCE.dialect,
        sourceTemplateId: REFERENCE_TCN_SOURCE.templateId,
        sourceTemplate: REFERENCE_TCN_SOURCE.filename,
        sourceByteLength: REFERENCE_TCN_SOURCE.byteLength,
        sourceSha256: REFERENCE_TCN_SOURCE.sha256,
        toolTableId: String(REFERENCE_TCN_SOURCE.toolId),
        toolId: REFERENCE_TCN_SOURCE.toolId,
        customMacro: REFERENCE_TCN_SOURCE.macro,
        releaseState: tcnCompatibility.releaseState,
        artifactKind: tcnCompatibility.artifactKind,
        containsMachining: tcnCompatibility.containsMachining,
        fingerprint: tcnCompatibility.fingerprint,
        issueCodes: tcnCompatibility.issues.map(issue => issue.code),
        warnings: tcnCompatibility.warnings,
      },
    },
    validation: tcnCompatibility.issues,
    validationSummary: {
      isValid: !tcnCompatibility.issues.some(issue => issue.level === 'error'),
      issueCodes: tcnCompatibility.issues.map(issue => issue.code),
    },
    requestedOutputs: {
      quotePdf: config.includeQuote,
      technicalDrawingPdf: config.includeDrawing,
      configurationJson: config.includeConfigFile,
      tcn: config.includeTcnFile,
    },
    estimate: {
      pricingVersion: estimate.pricingVersion,
      currency: estimate.currency,
      quantity: config.quantity,
      vatRate: estimate.vatRate,
      breakdown: {
        material: estimate.material,
        machining: estimate.machining,
        setupOnce: estimate.setup,
        finishing: estimate.finishing,
        vat: estimate.vat,
      },
      priceExcludingVat: estimate.subtotal,
      priceIncludingVat: estimate.total,
      pricePerUnitExcludingVat: estimate.unitPriceExcludingVat,
      pricePerUnitIncludingVat: estimate.unitPrice,
      leadTime: estimate.leadTime,
      disclaimer: 'Orientačný výpočet, finálna cena podlieha technologickej kontrole.',
    },
  };
}
